import { supabase } from './supabase';
import { AIAnalysisService, AnalysisResult } from './ai-analysis';

export interface RecentAnalysisItem {
  id: string;
  creativeName: string;
  creativeType: 'image' | 'video';
  thumbnail: string;
  analysisDate: string;
  overallScore: number;
  status: 'approved' | 'warning' | 'rejected';
  creditsUsed: number;
  version: string;
  platforms?: Record<string, { score: number; status: string }>;
  mainIssues?: string[];
  project?: string;
  analyst?: string;
  familyName?: string;
  parentCreativeId?: string;
  versionCount?: number;
  versions?: RecentAnalysisItem[];
}

export class RecentAnalysesService {
  /**
   * Get recent analyses from database
   */
  static async getRecentAnalyses(limit: number = 10): Promise<RecentAnalysisItem[]> {
    try {
      
      // Get current user data to check for organization
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.warn('User not authenticated in RecentAnalysesService');
        return [];
      }

      const { data: currentUser } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      let userIds = [user.id];
      
      // If user belongs to an organization, include all organization members' projects
      if (currentUser?.organization_id) {
        const { data: orgMembers } = await supabase
          .from('users')
          .select('id')
          .eq('organization_id', currentUser.organization_id);
        
        if (orgMembers && orgMembers.length > 0) {
          userIds = orgMembers.map(m => m.id);
        }
      }

      // Get projects from user or organization members
      const { data: userProjects } = await supabase
        .from('projects')
        .select('id')
        .in('user_id', userIds);

      const projectIds = userProjects?.map(p => p.id) || [];

      // Get creatives from projects AND unassigned creatives belonging to users
      let creativeIds: string[] = [];
      
      // First, get creatives from projects (if any)
      if (projectIds.length > 0) {
        const { data: projectCreatives } = await supabase
          .from('creatives')
          .select('id')
          .in('project_id', projectIds);
        
        if (projectCreatives && projectCreatives.length > 0) {
          creativeIds.push(...projectCreatives.map(c => c.id));
        }
      }
      
      // Then, get unassigned creatives (project_id = NULL) belonging to users
      const { data: unassignedCreatives } = await supabase
        .from('creatives')
        .select('id')
        .in('user_id', userIds)
        .is('project_id', null);
      
      if (unassignedCreatives && unassignedCreatives.length > 0) {
        creativeIds.push(...unassignedCreatives.map(c => c.id));
      }

      if (creativeIds.length === 0) {
        return [];
      }

      
      // Remove duplicates (in case a creative appears in both queries)
      creativeIds = [...new Set(creativeIds)];

      // Get analyses for these creatives with creative version info
      const { data, error } = await supabase
        .from('analyses')
        .select(`
          id,
          file_name,
          creative_type,
          creative_url,
          overall_score,
          status,
          created_at,
          analysis_data,
          platforms,
          creative_id,
          platform_analyses (
            platform,
            compliance_score,
            performance_score,
            status
          ),
          creatives!analyses_creative_id_fkey (
            version_number,
            version_name,
            family_name,
            parent_creative_id
          )
        `)
        .in('creative_id', creativeIds)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('❌ Error fetching recent analyses:', error);
        throw error;
      }

      if (!data) {
        return [];
      }

      
      // Transform data with error handling for individual items
      const transformedData: RecentAnalysisItem[] = [];
      data.forEach((item: any, index: number) => {
        try {
          const transformed = this.transformAnalysisData(item);
          transformedData.push(transformed);
        } catch (error) {
          console.error(`Error transforming analysis ${index}:`, error, item);
          // Skip problematic items rather than failing the entire request
        }
      });

      return transformedData;
    } catch (error) {
      console.error('❌ Error getting recent analyses:', error);
      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  }

  /**
   * Transform database analysis data to RecentAnalysisItem format
   */
  private static transformAnalysisData(dbItem: any): RecentAnalysisItem {
    try {
      const analysisData = dbItem.analysis_data as AnalysisResult;
      
      // Extract platforms data with error handling
      const platforms: Record<string, { score: number; status: string }> = {};
      if (dbItem.platform_analyses && Array.isArray(dbItem.platform_analyses)) {
        dbItem.platform_analyses.forEach((pa: any) => {
          if (pa && pa.platform) {
            const complianceScore = pa.compliance_score || 0;
            const performanceScore = pa.performance_score || 0;
            platforms[pa.platform] = {
              score: Math.round((complianceScore + performanceScore) / 2),
              status: pa.status || 'needs_review'
            };
          }
        });
      }

    // Determine overall status based on score and issues
    let status: 'approved' | 'warning' | 'rejected' = 'approved';
    const overallScore = dbItem.overall_score || 0;
    
    if (overallScore < 60) {
      status = 'rejected';
    } else if (overallScore < 80) {
      status = 'warning';
    }

    // Check for critical issues to override status
    if (analysisData?.detectedIssues) {
      const criticalIssues = analysisData.detectedIssues.filter(
        issue => issue.severity === 'critical' || issue.severity === 'high'
      );
      if (criticalIssues.length > 0) {
        status = overallScore < 70 ? 'rejected' : 'warning';
      }
    }

    // Extract main issues
    const mainIssues: string[] = [];
    if (analysisData?.detectedIssues) {
      const topIssues = analysisData.detectedIssues
        .filter(issue => issue.severity === 'critical' || issue.severity === 'high')
        .slice(0, 3)
        .map(issue => issue.title);
      mainIssues.push(...topIssues);
    }

      return {
        id: dbItem.id,
        creativeName: dbItem.file_name || 'Untitled Creative',
        creativeType: dbItem.creative_type as 'image' | 'video',
        thumbnail: this.generateThumbnailUrl(dbItem.creative_url, dbItem.creative_type),
        analysisDate: dbItem.created_at,
        overallScore: overallScore,
        status,
        creditsUsed: 1, // Always 1 credit per analysis in the new pricing model
        version: dbItem.creatives?.version_number ? `v${dbItem.creatives.version_number}` : 'v1',
        platforms,
        mainIssues,
        project: 'Default Project', // TODO: Connect to actual project data
        analyst: 'AI Analysis', // AI-generated analysis
        familyName: dbItem.creatives?.family_name,
        parentCreativeId: dbItem.creatives?.parent_creative_id
      };
    } catch (error) {
      console.error('Error transforming analysis data:', error, dbItem);
      // Return a fallback item with safe defaults
      return {
        id: dbItem.id || 'unknown',
        creativeName: dbItem.file_name || 'Error Loading Creative',
        creativeType: 'image',
        thumbnail: 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=400',
        analysisDate: dbItem.created_at || new Date().toISOString(),
        overallScore: 0,
        status: 'warning' as const,
        creditsUsed: 1,
        version: dbItem.creatives?.version_number ? `v${dbItem.creatives.version_number}` : 'v1',
        platforms: {},
        mainIssues: ['Error loading analysis data'],
        project: 'Default Project',
        analyst: 'AI Analysis',
        familyName: dbItem.creatives?.family_name,
        parentCreativeId: dbItem.creatives?.parent_creative_id
      };
    }
  }

  /**
   * Generate thumbnail URL for creative
   */
  private static generateThumbnailUrl(creativeUrl: string, creativeType: string): string {
    if (!creativeUrl) {
      // Fallback to placeholder if no URL provided
      if (creativeType === 'video') {
        return 'https://images.pexels.com/photos/3747463/pexels-photo-3747463.jpeg?auto=compress&cs=tinysrgb&w=400';
      } else {
        return 'https://images.pexels.com/photos/1029614/pexels-photo-1029614.jpeg?auto=compress&cs=tinysrgb&w=400';
      }
    }

    if (creativeType === 'video') {
      // For videos, try to get the thumbnail URL
      // The thumbnail should be stored in the thumbnails bucket with a similar path structure
      try {
        // Extract the file path from the creative URL
        const url = new URL(creativeUrl);
        const pathParts = url.pathname.split('/');
        
        // Find the file path part (after /storage/v1/object/public/creatives/)
        const creativesIndex = pathParts.findIndex(part => part === 'creatives');
        if (creativesIndex !== -1 && creativesIndex < pathParts.length - 1) {
          const filePath = pathParts.slice(creativesIndex + 1).join('/');
          const fileName = pathParts[pathParts.length - 1];
          const fileNameWithoutExt = fileName.split('.')[0];
          
          // Construct thumbnail URL
          const thumbnailPath = filePath.replace(fileName, `thumb_${fileNameWithoutExt}.jpg`);
          const thumbnailUrl = creativeUrl.replace('/creatives/', '/thumbnails/').replace(fileName, `thumb_${fileNameWithoutExt}.jpg`);
          
          return thumbnailUrl;
        }
      } catch (error) {
        console.warn('Failed to generate video thumbnail URL:', error);
      }
      
      // Fallback to placeholder for videos if thumbnail generation fails
      return 'https://images.pexels.com/photos/3747463/pexels-photo-3747463.jpeg?auto=compress&cs=tinysrgb&w=400';
         } else {
       // For images, use the actual creative URL as thumbnail
       // The browser will handle resizing via CSS
       return creativeUrl;
     }
  }

  /**
   * Get analysis statistics
   */
  static async getAnalysisStats(): Promise<{
    totalAnalyses: number;
    averageScore: number;
    creditsUsed: number;
    creativeFamilies: number;
  }> {
    try {
      // Get current user data to check for organization
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: currentUser } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      let userIds = [user.id];
      
      // If user belongs to an organization, include all organization members' projects
      if (currentUser?.organization_id) {
        const { data: orgMembers } = await supabase
          .from('users')
          .select('id')
          .eq('organization_id', currentUser.organization_id);
        
        if (orgMembers && orgMembers.length > 0) {
          userIds = orgMembers.map(m => m.id);
        }
      }

      // Get projects from user or organization members
      const { data: userProjects } = await supabase
        .from('projects')
        .select('id')
        .in('user_id', userIds);

      const projectIds = userProjects?.map(p => p.id) || [];

      // Get creatives from projects AND unassigned creatives belonging to users
      let creativeIds: string[] = [];
      
      // First, get creatives from projects (if any)
      if (projectIds.length > 0) {
        const { data: projectCreatives } = await supabase
          .from('creatives')
          .select('id')
          .in('project_id', projectIds);
        
        if (projectCreatives && projectCreatives.length > 0) {
          creativeIds.push(...projectCreatives.map(c => c.id));
        }
      }
      
      // Then, get unassigned creatives (project_id = NULL) belonging to users
      const { data: unassignedCreatives } = await supabase
        .from('creatives')
        .select('id')
        .in('user_id', userIds)
        .is('project_id', null);
      
      if (unassignedCreatives && unassignedCreatives.length > 0) {
        creativeIds.push(...unassignedCreatives.map(c => c.id));
      }

      if (creativeIds.length === 0) {
        return {
          totalAnalyses: 0,
          averageScore: 0,
          creditsUsed: 0,
          creativeFamilies: 0
        };
      }

      // Remove duplicates (in case a creative appears in both queries)
      creativeIds = [...new Set(creativeIds)];

      // Get analyses for these creatives with family information
      const { data, error } = await supabase
        .from('analyses')
        .select(`
          overall_score, 
          platforms,
          creatives!analyses_creative_id_fkey (
            family_name
          )
        `)
        .in('creative_id', creativeIds);

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        return {
          totalAnalyses: 0,
          averageScore: 0,
          creditsUsed: 0,
          creativeFamilies: 0
        };
      }

      const totalAnalyses = data.length;
      const averageScore = Math.round(
        data.reduce((sum, item) => sum + (item.overall_score || 0), 0) / totalAnalyses
      );
      const creditsUsed = totalAnalyses; // 1 credit per analysis
      
      // Get unique creative families across all analyses
      const allFamilies = new Set<string>();
      data.forEach(item => {
        const familyName = item.creatives?.family_name || 'Unknown Family';
        allFamilies.add(familyName);
      });

      return {
        totalAnalyses,
        averageScore,
        creditsUsed,
        creativeFamilies: allFamilies.size
      };
    } catch (error) {
      console.error('Error getting analysis stats:', error);
      throw error;
    }
  }

  /**
   * Get filtered and sorted analyses with pagination support
   */
  static async getFilteredAnalyses(options: {
    searchTerm?: string;
    groupByFamily?: boolean;
    filterStatus?: string;
    sortBy?: 'date' | 'score' | 'name';
    limit?: number;
    offset?: number;
  }): Promise<RecentAnalysisItem[]> {
    try {
      // Get current user data to check for organization
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: currentUser } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      let userIds = [user.id];
      
      // If user belongs to an organization, include all organization members' projects
      if (currentUser?.organization_id) {
        const { data: orgMembers } = await supabase
          .from('users')
          .select('id')
          .eq('organization_id', currentUser.organization_id);
        
        if (orgMembers && orgMembers.length > 0) {
          userIds = orgMembers.map(m => m.id);
        }
      }

      // Get projects from user or organization members
      const { data: userProjects } = await supabase
        .from('projects')
        .select('id')
        .in('user_id', userIds);

      const projectIds = userProjects?.map(p => p.id) || [];

      // Get creatives from projects AND unassigned creatives belonging to users
      let creativeIds: string[] = [];
      
      // First, get creatives from projects (if any)
      if (projectIds.length > 0) {
        const { data: projectCreatives } = await supabase
          .from('creatives')
          .select('id')
          .in('project_id', projectIds);
        
        if (projectCreatives && projectCreatives.length > 0) {
          creativeIds.push(...projectCreatives.map(c => c.id));
        }
      }
      
      // Then, get unassigned creatives (project_id = NULL) belonging to users
      const { data: unassignedCreatives } = await supabase
        .from('creatives')
        .select('id')
        .in('user_id', userIds)
        .is('project_id', null);
      
      if (unassignedCreatives && unassignedCreatives.length > 0) {
        creativeIds.push(...unassignedCreatives.map(c => c.id));
      }

      if (creativeIds.length === 0) {
        return [];
      }

      // Remove duplicates (in case a creative appears in both queries)
      creativeIds = [...new Set(creativeIds)];

      let query = supabase
        .from('analyses')
        .select(`
          id,
          file_name,
          creative_type,
          creative_url,
          overall_score,
          status,
          created_at,
          analysis_data,
          platforms,
          creative_id,
          platform_analyses (
            platform,
            compliance_score,
            performance_score,
            status
          ),
          creatives!analyses_creative_id_fkey (
            version_number,
            version_name,
            family_name,
            parent_creative_id
          )
        `)
        .in('creative_id', creativeIds);

      // Apply search filter
      if (options.searchTerm) {
        query = query.ilike('file_name', `%${options.searchTerm}%`);
      }

      // Apply sorting
      switch (options.sortBy) {
        case 'score':
          query = query.order('overall_score', { ascending: false });
          break;
        case 'name':
          query = query.order('file_name', { ascending: true });
          break;
        case 'date':
        default:
          query = query.order('created_at', { ascending: false });
          break;
      }

      // If grouping by family, fetch more data to ensure complete families
      // Otherwise, apply regular pagination
      if (options.groupByFamily) {
        // Fetch a larger dataset to ensure complete families (200 items max)
        query = query.limit(200);
      } else {
        // Apply regular pagination for non-grouped view
        if (options.limit) {
          query = query.limit(options.limit);
        }
        
        if (options.offset) {
          query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
        }
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      if (!data) {
        return [];
      }

      let analyses = data.map(item => this.transformAnalysisData(item));

      // Apply status filter (done client-side since it's computed)
      if (options.filterStatus && options.filterStatus !== 'all') {
        analyses = analyses.filter(analysis => analysis.status === options.filterStatus);
      }

      // Apply family grouping if requested
      if (options.groupByFamily) {
        analyses = this.groupAnalysesByFamily(analyses);
        
        // Apply pagination to grouped results
        const limit = options.limit || 10;
        const offset = options.offset || 0;
        analyses = analyses.slice(offset, offset + limit);
      }

      return analyses;
    } catch (error) {
      console.error('Error getting filtered analyses:', error);
      throw error;
    }
  }

  /**
   * Group analyses by family, showing only the latest version per family
   */
  private static groupAnalysesByFamily(analyses: RecentAnalysisItem[]): RecentAnalysisItem[] {
    // Group analyses by root creative ID (for version relationships)
    const familyGroups = new Map<string, RecentAnalysisItem[]>();
    
    analyses.forEach(analysis => {
      // Use parent creative ID if it exists (for versions), otherwise use the creative ID itself (for root)
      const rootCreativeId = analysis.parentCreativeId || analysis.id;
      
      // For consistent grouping, we'll use the family name if available, 
      // otherwise fall back to the root creative ID
      let groupKey = analysis.familyName;
      if (!groupKey || groupKey.trim() === '') {
        groupKey = rootCreativeId;
      }
      
      if (!familyGroups.has(groupKey)) {
        familyGroups.set(groupKey, []);
      }
      familyGroups.get(groupKey)!.push(analysis);
    });

    // For each family, return the latest analysis with version info
    const groupedAnalyses: RecentAnalysisItem[] = [];
    
    familyGroups.forEach((familyAnalyses, familyName) => {
      // Sort by date (most recent first)
      const sortedFamilyAnalyses = familyAnalyses.sort((a, b) => 
        new Date(b.analysisDate).getTime() - new Date(a.analysisDate).getTime()
      );
      
      const latestAnalysis = sortedFamilyAnalyses[0];
      const versionCount = familyAnalyses.length;
      
      // If there's only one version, return as-is
      if (versionCount === 1) {
        groupedAnalyses.push(latestAnalysis);
      } else {
        // Create a grouped analysis item
        // Use the family name if available, otherwise use the latest analysis creative name
        const displayName = latestAnalysis.familyName || latestAnalysis.creativeName;
        const groupedAnalysis: RecentAnalysisItem = {
          ...latestAnalysis,
          creativeName: displayName,
          versionCount: versionCount,
          versions: sortedFamilyAnalyses
        };
        groupedAnalyses.push(groupedAnalysis);
      }
    });

    // Sort grouped analyses by the latest analysis date
    return groupedAnalyses.sort((a, b) => 
      new Date(b.analysisDate).getTime() - new Date(a.analysisDate).getTime()
    );
  }

  /**
   * Check if there are more analyses available for pagination
   */
  static async hasMoreAnalyses(options: {
    searchTerm?: string;
    groupByFamily?: boolean;
    filterStatus?: string;
    currentCount: number;
  }): Promise<boolean> {
    try {
      // If we're grouping by family, we need to fetch more data to determine the actual count
      // of grouped families after filtering
      if (options.groupByFamily) {
        const allAnalyses = await this.getFilteredAnalyses({
          searchTerm: options.searchTerm,
          groupByFamily: true,
          filterStatus: options.filterStatus,
          sortBy: 'date',
          // Don't apply pagination to get the full count
        });
        
        return allAnalyses.length > options.currentCount;
      }

      // For non-grouped view, use the simpler count-based approach
      
      // Get current user data to check for organization
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: currentUser } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      let userIds = [user.id];
      
      // If user belongs to an organization, include all organization members' projects
      if (currentUser?.organization_id) {
        const { data: orgMembers } = await supabase
          .from('users')
          .select('id')
          .eq('organization_id', currentUser.organization_id);
        
        if (orgMembers && orgMembers.length > 0) {
          userIds = orgMembers.map(m => m.id);
        }
      }

      // Get projects from user or organization members
      const { data: userProjects } = await supabase
        .from('projects')
        .select('id')
        .in('user_id', userIds);

      const projectIds = userProjects?.map(p => p.id) || [];

      // Get creatives from projects AND unassigned creatives belonging to users
      let creativeIds: string[] = [];
      
      // First, get creatives from projects (if any)
      if (projectIds.length > 0) {
        const { data: projectCreatives } = await supabase
          .from('creatives')
          .select('id')
          .in('project_id', projectIds);
        
        if (projectCreatives && projectCreatives.length > 0) {
          creativeIds.push(...projectCreatives.map(c => c.id));
        }
      }
      
      // Then, get unassigned creatives (project_id = NULL) belonging to users
      const { data: unassignedCreatives } = await supabase
        .from('creatives')
        .select('id')
        .in('user_id', userIds)
        .is('project_id', null);
      
      if (unassignedCreatives && unassignedCreatives.length > 0) {
        creativeIds.push(...unassignedCreatives.map(c => c.id));
      }

      if (creativeIds.length === 0) {
        return false;
      }

      // Remove duplicates
      creativeIds = [...new Set(creativeIds)];

      let query = supabase
        .from('analyses')
        .select('id', { count: 'exact', head: true })
        .in('creative_id', creativeIds);

      // Apply search filter
      if (options.searchTerm) {
        query = query.ilike('file_name', `%${options.searchTerm}%`);
      }

      const { count, error } = await query;

      if (error) {
        throw error;
      }

      return (count || 0) > options.currentCount;
    } catch (error) {
      console.error('Error checking for more analyses:', error);
      return false;
    }
  }

  /**
   * Delete analysis
   */
  static async deleteAnalysis(analysisId: string): Promise<void> {
    try {
      // Delete platform analyses first due to foreign key constraint
      const { error: platformError } = await supabase
        .from('platform_analyses')
        .delete()
        .eq('analysis_id', analysisId);

      if (platformError) {
        throw platformError;
      }

      // Delete main analysis
      const { error } = await supabase
        .from('analyses')
        .delete()
        .eq('id', analysisId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting analysis:', error);
      throw error;
    }
  }

  /**
   * Rename analysis (update file_name)
   */
  static async renameAnalysis(analysisId: string, newName: string): Promise<RecentAnalysisItem | null> {
    try {
      if (!newName.trim()) {
        throw new Error('Analysis name cannot be empty');
      }

      const { data, error } = await supabase
        .from('analyses')
        .update({ 
          file_name: newName.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', analysisId)
        .select(`
          id,
          file_name,
          creative_type,
          creative_url,
          overall_score,
          status,
          created_at,
          analysis_data,
          platforms,
          platform_analyses (
            platform,
            compliance_score,
            performance_score,
            status
          )
        `)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return null;
      }

      return this.transformAnalysisData(data);
    } catch (error) {
      console.error('Error renaming analysis:', error);
      throw error;
    }
  }
} 